<template>
    <div class="flex flex-col lg:flex-row justify-between items-center">
        <h2 class="text-xl leading-9 font-headline font-medium text-bhav-blue-600">Sales Team</h2>
        <div v-if="GlobalPolicy.edit()" class="flex gap-2">
            <FormButton color="blue" prefix-class="fak" icon="fa-link" type="button" @click="openSync()">
                Attach
            </FormButton>
            <FormButton color="blue" prefix-class="fak" icon="fa-plus" type="button" @click="create()">
                Create
            </FormButton>
        </div>
    </div>
    <PaginationTable v-if="agents" :data="agents" :columns="columns" :view="`${$route.name}-${$route.params.tab}`" :legacy="false">
        <template #col-name="{row}">
            <p class="flex gap-2 items-center py-4" :class="row.status !== 'active' && 'opacity-50'">
                <span class="rounded-full w-10 h-10 border border-oakwood-gray-100 overflow-hidden flex items-center justify-center bg-bhav-blue-400 text-white">
                    <img v-if="row?.photo_url" :src="row.photo_url" class="aspect-square object-cover" />
                    <span v-else class="fas fa-user"></span>
                </span>
                <span class="text-sm leading-5 font-medium text-oakwood-gray-900">
                    {{ row?.first_name }} {{ row?.last_name }}
                </span>
            </p>
        </template>

        <template #col-email="{row}">
            <a v-if="row?.email" :href="`mailto:${row.email}`" class="block py-4 text-bhav-blue-600 hover:text-bhav-blue-700" :class="row.status !== 'active' && 'opacity-50'">
                {{ row.email }}
            </a>
        </template>

        <template #col-phone="{row}">
            <a v-if="row?.meta?.phone" :href="`tel:${row.meta.phone}`" class="block py-4 text-bhav-blue-600 hover:text-bhav-blue-700" :class="row.status !== 'active' && 'opacity-50'">
                {{ row.meta.phone }}
            </a>
        </template>

        <template #col-actions="{row, i}">
            <div v-if="GlobalPolicy.edit()" class="flex gap-2 justify-start items-center py-4">
                <label :for="`item-${row.id}-status`">{{ statuses.find(status => status.value == row.status).label }}</label>
                <ToggleButton :id="`item-${row.id}-status`" @changed="status(row)" :checked="row.status == 'active'"
                    :label="row.status == 'active' ? 'Active' : 'Inactive'"/>
                <FormButton type="button" @click="edit(row)" title="Edit Sales Agent" :disabled="row.status !== 'active'" icon="fa-pen"  color="blue" prefix-class="fak">
                    <span class="sr-only">Edit</span>
                </FormButton>
                <FormButton type="button" @click="openDetach(row)" title="Detach Sales Agent" prefix-class="fak" icon="fa-detach" color="red">
                    <span class="sr-only">Detach</span>
                </FormButton>
                <FormButton type="button" @click="openDelete(row)" title="Permanently Delete Sales Agent" prefix-class="fak" icon="fa-trash" color="red"/>
            </div>
            <div v-else class="py-4">
                {{ statuses.find(status => status.value == row.status).label }}
            </div>
        </template>
    </PaginationTable>

    <ModalDialog v-if="modal" :no-padding="true" :open="modal" @close="cancel">
        <template #header>
            <div>
                <h3 class="text-lg leading-6 font-medium text-bhav-blue-500">
                    Sales Team Member Editor
                </h3>
                <p class="mt-1 max-w-2xl text-sm leading-5 text-oakwood-gray-500">
                    Enter the details about sales team member you wish to include with this collection.
                </p>
            </div>
        </template>

        <template #body>
            <div class="mt-6 sm:mt-5">
                <div class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <p class="block text-sm font-medium leading-5 text-bhav-blue-300 sm:mt-px sm:pt-2">
                        Photo
                    </p>
                    <UploadFiles @attach="attach" v-model="photo_url" :multiple="false" class="col-span-2" />
                </div>

                <div
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="first_name"
                        class="block text-sm font-medium leading-5 text-bhav-blue-300 sm:mt-px sm:pt-2">
                        First Name <span class="text-oakwood-red" title="Value is required">*</span>
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <div class="rounded-md">
                            <TextInput id="first_name" v-model="agent.first_name"/>
                        </div>
                    </div>
                </div>

                <div
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="last_name"
                        class="block text-sm font-medium leading-5 text-bhav-blue-300 sm:mt-px sm:pt-2">
                        Last Name <span class="text-oakwood-red" title="Value is required">*</span>
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <div class="rounded-md">
                            <TextInput id="last_name" v-model="agent.last_name"/>
                        </div>
                    </div>
                </div>

                <div
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="email"
                        class="block text-sm font-medium leading-5 text-bhav-blue-300 sm:mt-px sm:pt-2">
                        Email
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <div class="rounded-md">
                            <TextInput id="email" v-model="agent.email" type="email"/>
                        </div>
                    </div>
                </div>

                <div
                    class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-oakwood-gray-200 sm:py-5">
                    <label for="phone"
                        class="block text-sm font-medium leading-5 text-bhav-blue-300 sm:mt-px sm:pt-2">
                        Phone <span class="text-oakwood-red" title="Value is required">*</span>
                    </label>
                    <div class="mt-1 sm:mt-0 sm:col-span-2">
                        <div class="rounded-md">
                            <PhoneNumber id="phone" v-model="agent.meta.phone">
                            </PhoneNumber>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <template #buttons>
            <FormButton :disabled="!valid" type="button" @click="save" color="blue">
                Save
            </FormButton>
            <FormButton type="button" @click="cancel" color="gray">
                Cancel
            </FormButton>
        </template>
    </ModalDialog>

    <ModalDialog v-if="syncing" :no-padding="true" :open="syncing" @close="cancel">
        <template #header>
            <div>
                <h3 class="text-lg leading-6 font-medium text-bhav-blue-500">
                Attach an Existing Sales Team Member
                </h3>
                <p class="mt-1 max-w-2xl text-sm leading-5 text-oakwood-gray-500">
                Select one or more sales team members to attach.
                </p>
            </div>
        </template>

        <template #body>
            <nav class="grid md:grid-cols-4">
                <div class=" md:col-span-2 mb-4 flex-initial">
                    <label for="search" class="block text-sm font-medium leading-5 pb-1.5 text-blue-500">Search by Keyword</label>
                    <SearchInput v-model="agent_query" color="gray" placeholder="Search..." />
                </div>
            </nav>
            <PaginationGrid :data="filtered">
                <template #card="{ items }">
                    <li v-for="agent,i in items" :key="i" class="rounded overflow-hidden"
                    :class="!sync_agents.includes(agent) ? 'border border-oakwood-gray-100 bg-white' : 'border-2 border-bhav-blue-600 bg-bhav-blue-50'">
                    <label :for="`agent-${i}`" class="p-6 flex flex-col gap-2">
                        <figure aria-hidden="true" class="rounded-full overflow-hidden bg-bhav-blue-400 text-blue-300 flex items-center justify-center w-full aspect-square">
                            <img v-if="agent.photo_url" :src="agent.photo_url" class="aspect-square object-cover" />
                            <div v-else class="far fa-user text-5xl"></div>
                        </figure>
                        <p class="text-center text-bhav-blue-500 font-medium">{{ agent.first_name }} {{ agent.last_name }}</p>
                    </label>
                    <input class="sr-only" type="checkbox" :id="`agent-${i}`" v-model="sync_agents" :value="agent" />
                </li>
                </template>
                <template #none>
                    There are no available sales team members to attach.
                </template>
            </PaginationGrid>
        </template>

        <template #buttons>
            <FormButton :disabled="sync_agents.length == 0" type="button" @click="sync" color="blue">
                Attach
            </FormButton>
            <FormButton type="button" @click="cancel" color="gray">
                Cancel
            </FormButton>
        </template>
    </ModalDialog>

    <ModalDialog v-if="detaching" :no-padding="true" :open="detaching" @close="cancel">
        <template #header>
            <h2 class="text-lg text-bhav-blue-500 font-semibold">Confirm Sales Team Member Detachment</h2>
        </template>
        <template #body>
            <p class="text-bhav-blue-900"><strong>Attention!</strong> You are about to detach <em>{{ agent?.first_name }} {{ agent?.last_name }}</em>. Are you sure?</p>
        </template>
        <template #buttons>
            <FormButton type="button" @click="detach" color="blue">
                Detach
            </FormButton>
            <FormButton type="button" @click="cancel" color="gray">
                Cancel
            </FormButton>
        </template>
    </ModalDialog>

    <ModalDialog v-if="deleting" :no-padding="true" :open="deleting" @close="cancel">
        <template #header>
            <h2 class="text-lg text-bhav-blue-500 font-semibold">Confirm Sales Team Member Deletion</h2>
        </template>
        <template #body>
            <p class="text-oakwood-gray-900"><strong>Attention!</strong> You are about to delete <em>{{ agent?.first_name }} {{ agent?.last_name }}</em>.</p>
            <p class="mt-2"><strong>This will detach this person from all collections and <em>permanently</em> delete their record.</strong></p>
            <p class="mt-2">Are you sure?</p>
        </template>
        <template #buttons>
            <FormButton type="button" @click="purge" color="blue">
                Delete
            </FormButton>
            <FormButton type="button" @click="cancel" color="gray">
                Cancel
            </FormButton>
        </template>
    </ModalDialog>
</template>

<script>
import {FormButton, UploadFiles, PhoneNumber, ToggleButton, TextInput} from '@/components/form';
import ModalDialog from '@/components/ModalDialog.vue';
import PaginationTable from '../PaginationTable.vue';
import axios from 'axios';
import Agent from '@/models/agent';
import { agentStatuses } from '@/helpers/globals';
import { GlobalPolicy } from '@/policies/global';
import PaginationGrid from '../PaginationGrid.vue';
import SearchInput from '../form/SearchInput.vue';

export default {
    setup() {
        return {
            GlobalPolicy
        }
    },
    data() {
        return {
            statuses: agentStatuses,
            modal: false,
            index: false,
            agent: new Agent,
            detaching: false,
            deleting: false,
            syncing: false,
            sync_agents: [],
            available_agents: [],
            agent_query: ''
        }
    },
    emits: [
        'update'
    ],
    methods: {
        attach(files) {
            this.agent.file = files[0];
        },
        edit(agent) {
            this.modal = 1;
            this.agent = new Agent(agent);
            this.index = agent.meta?.order ?? this.agents.length;
        },
        create() {
            this.modal = 1;
            this.agent = new Agent;
            this.index = this.agents.length;
        },
        sync() {
            if(!this.sync_agents.length) 
                return;
            let next = this.agents.length;
            this.sync_agents.forEach(async (agent,i) => {
                await axios.patch(`collections/${this.$route.params.id}/agents/${agent.id}`, {...agent, meta: { order: (i + next) }})
                    .then(res => {
                        this.$emit('update', res.data);
                    })
                    .catch(err => {
                        console.error(err);
                    })
            });         
            this.cancel();
        },
        save() {
            let data = {
                ...this.agent,
                meta: {order: this.index, ...this.agent.meta}
            };

            let handler = data.id ? 
                axios.postForm(`collections/${this.$route.params.id}/agents/${data.id}?_method=PATCH`, data) : 
                axios.postForm(`collections/${this.$route.params.id}/agents`, data);

            handler
            .then(res => {
                this.$emit('update', [...this.agents,res.data]);
                this.cancel();
            })
            .catch(err => {
                console.error(err);
                this.cancel();
            });
        },
        status(item) {
            if(item.status == 'active') {
                axios.delete(`agents/${item.id}`)
                .then(() => {
                    this.$emit('update', this.agents.filter(agent => agent.id !== item.id));
                })
                .catch(err => {
                    console.error(err);
                })
            } else {
                axios.patch(`agents/${item.id}/restore`)
                .then(res => {
                    this.$emit('update', [...this.agents, res.data]);
                })
                .catch(err => {
                    console.error(err);
                })
            }
        },
        getAgents() {
            axios.get(`agents`)
                .then(res => {
                    this.available_agents = res.data;
                });
        },
        detach() {
            if(!this.agent?.id) {
                console.error('You must select a sales agent to detach');
                return;
            }
            axios.delete(`collections/${this.$route.params.id}/agents/${this.agent.id}`)
                .then(res => {
                    this.$emit('update', res.data);
                    this.cancel();                    
                })
                .catch(err => {
                    console.error(err);
                });            
        },
        purge() {
            if(!this.agent?.id) {
                console.error('You must select a sales agent to delete');
                return;
            }
            axios.delete(`agents/${this.agent.id}/purge`)
            .then(() => {
                this.$emit('update', this.agents.filter(agent => agent.id !== this.agent.id));
                this.cancel();
            })
            .catch(err => {
                console.error(err);
            })
        },
        openDetach(agent) {
            this.agent = new Agent(agent);
            this.detaching = true;
        },
        openDelete(agent) {
            this.agent = new Agent(agent);
            this.deleting = true;
        },
        openSync() {
            this.getAgents();
            this.syncing = true;
        },
        cancel() {
            this.agent = new Agent;
            this.index = false;
            this.modal = false;
            this.detaching = false;
            this.deleting = false;
            this.syncing = false;
            this.agent_query = '';
        }
    },
    computed: {
        photo_url: {
            get() {
                if(this.agent.photo_url) {
                    return [this.agent.photo_url]
                } else {
                    return this.agent.file == null ? [] : [this.agent.file]
                }
            },
            set(value) {
                this.agent.photo_url = null;
                this.agent.photo = null;
                this.agent.file = value[0];
            }
        },
        valid() {
            return this.agent.first_name &&
                this.agent.last_name &&
                this.agent.meta.phone;
        },
        filtered() {
            let results = [...this.available_agents];

            results = results.filter(agent => !this.agents.map(agent => agent.id).includes(agent.id) && agent.status == 'active');

            if(this.agent_query) {
                results = results.filter(agent => JSON.stringify(agent).toLowerCase().includes(this.agent_query.toLowerCase()))
            }

            return results;
        },
        columns() {
            return [
                {
                    name: 'name',
                    label: 'Name',
                    sort: (row) => `${row.first_name} ${row.last_name}`,
                    default: true
                },
                {
                    name: 'email',
                    label: 'Email',
                    sort: (row) => row.email || '',
                    default: true
                },
                {
                    name: 'phone',
                    label: 'Phone',
                    sort: (row) => row.meta?.phone || '',
                    default: true
                },
                {
                    name: 'actions',
                    label: GlobalPolicy.edit() ? 'Actions' : 'Status',
                    sort: false,
                    default: true,
                    class: 'text-left'
                }
            ]
        }
    },
    props: {
        agents: {
            type: Array,
            required: true,
            default: () => []
        }
    },
    components: {
        FormButton, ModalDialog, PaginationTable, TextInput,
        UploadFiles, PhoneNumber, ToggleButton, PaginationGrid,
        SearchInput
    }
}
</script>